import React, { useState, useRef, useEffect } from 'react';
import {
  Container,
  Checkbox,
  ExpandableSection,
  Header,
  FormField,
  SpaceBetween,
  Textarea,
  Button,
  Box,
  ButtonDropdown,
  Alert,
} from '@cloudscape-design/components';
import { InfoLink } from '../../commons/common-components';
import validateField from '../form-validation-config';
import { submissionsApi } from '../../../api/Submission';
import BroadcastSubscriptionAdapter from './BroadcastSubscriptionAdapter';
import { Spinner } from '@cloudscape-design/components';
import CustomModal from './custom-modal';

function DistributionsFooter({ state, onChange }) {
  return (
    <ExpandableSection headerText="Additional settings" variant="footer">
      <SpaceBetween size="l">
        <FormField label="IPv6">
          <Checkbox checked={state.ipv6isOn} onChange={({ detail: { checked } }) => onChange('ipv6isOn', checked)}>
            Turn on
          </Checkbox>
        </FormField>
      </SpaceBetween>
    </ExpandableSection>
  );
}

const isS3PermissionError = (attribute, errorText) =>
  attribute === 's3BucketSelectedOption' &&
  errorText ===
  "CloudFront isn't allowed to write logs to this bucket. You must enable access control lists (ACL) for the bucket.";

export default function DistributionPanel({
  loadHelpPanelContent,
  validation = false,
  data,
  // errors = {},
  setData,
  // setErrors,
  refs = {},
  isFramework2 = false,
  indicatorSubmissionID,
  setValidateEvidenceFiles,
  setJudgmentValidation,
  setValidateWordLimit,
}) {
  const [isCustomModalVisible, setIsCustomModalVisible] = useState(false);
  const [customModalMessage] = useState('Formal Arrangements could be policies, procedures, bylaws, handbooks or manuals with a clear description of the processs steps, the timeframe, the stakeholders involved, and their roles and responsibilities. Evidence on the implementation is required in case the formal arrangement is already implemented.');
  const [deletedFiles, setdeletedFiles] = useState([]);

  const [processingFiles, setprocessingFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [tokenLimitError, setTokenLimitError] = useState(null);
  const [tokenUsage, setTokenUsage] = useState(null);
  const fileInputRef = useRef(null);
  const [judgment, setJudgment] = useState(null);

  const [errors, setErrors] = useState({});
  //  const MAX_FILES_LIMIT = 5;

  // Remove duplicate files based on name, size, and lastModified
  const removeDuplicateFiles = (files) => {
    const seen = new Map();
    return files.filter(file => {
      const key = `${file.name}_${file.size}_${file.lastModified}`;
      if (seen.has(key)) return false;
      seen.set(key, true);
      return true;
    });
  };

  const onChange = (attribute, value) => {
    setData({ [attribute]: value });

    if (validation && errors[attribute]?.length > 0) {
      const { errorText } = validateField(attribute, value);

      if (isS3PermissionError(attribute, errorText)) {
        setErrors(prev => ({ ...prev, [attribute]: '' }));
      } else {
        setErrors(prev => ({ ...prev, [attribute]: errorText }));
      }
    } else {
      // clear error if input is now valid
      setErrors(prev => ({ ...prev, [attribute]: '' }));
    }

    // Validate judgment
    if (!judgment) {
      setErrors(prev => ({ ...prev, judgment: 'Please set judgment' }));
    } else {
      setErrors(prev => ({ ...prev, judgment: '' }));
    }
  };

  const onBlur = attribute => {
    if (!validation) {
      return;
    }

    const value = data[attribute];
    const { errorText } = validateField(attribute, value);

    if (isS3PermissionError(attribute, errorText)) {
      return;
    }

    setErrors({ [attribute]: errorText });
  };

  // Single Upload System: Upload only to original system (for AI functionality)
  // RMS sync will happen during final submission
  const uploadFilesToOriginalSystem = async (files, evidenceId, evidenceTitle) => {
    try {
      // Step 1: Remove duplicates
      const uniqueFiles = removeDuplicateFiles(files);

      if (uniqueFiles.length < files.length) {
        console.log(`Removed ${files.length - uniqueFiles.length} duplicate file(s)`);
      }

      const uploadedFiles = [];

      // Step 2: Upload to ORIGINAL SYSTEM only (for AI functionality)
      for (const file of uniqueFiles) {
        try {
          // Generate presigned URL for original system
          const urlResponse = await submissionsApi.generateEvidenceUploadUrl(
            indicatorSubmissionID,
            evidenceId,
            { filename: file.name }
          );

          // Upload to original S3
          await fetch(urlResponse.presigned_url, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': file.type || 'application/octet-stream'
            }
          });

          // Mark upload complete in original system
          await submissionsApi.markUploadComplete(urlResponse.upload_id);

          uploadedFiles.push({
            fileID: urlResponse.upload_id,
            s3_key: urlResponse.s3_key, // Store S3 key for later RMS sync
            file: {
              name: file.name,
              size: file.size,
              type: file.type,
              lastModified: file.lastModified
            }
          });

          console.log(`✅ Original system upload complete: ${file.name}`);
        } catch (error) {
          console.error(`❌ Original system upload failed for ${file.name}:`, error);
          throw new Error(`Original system upload failed for ${file.name}: ${error.message}`);
        }
      }

      return {
        success: true,
        uploadedFiles: uploadedFiles
      };

    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const validateFile = (file) => {
    const validationErrors = [];
    const allowedExtensions = ['.txt', '.csv', '.png', '.jpg', '.pdf', '.docx', '.xlsx'];
    const maxSize = 15 * 1024 * 1024; // MB
    const extension = '.' + file.name.split('.').pop().toLowerCase();
    if (file.size === 0) {
      validationErrors.push('Empty files are not allowed');
    }

    if (!allowedExtensions.includes(extension)) {
      validationErrors.push(`File extension ${extension} is not supported`);
    }

    if (process.env.REACT_APP_FRAMEWORK == 1 && file.size > maxSize) {
      validationErrors.push('File size exceeds 15MB limit');
    }

    return validationErrors;
  }

  const deleteFile = async (file, evidenceId) => {
    if (deletedFiles.some(pf => pf.fileName === file.file.name)) return;

    setdeletedFiles([{ fileName: file.file.name, evidenceId }]);

    try {
      await submissionsApi.deleteUploadedFile(file.fileID);

      setData({
        ...data,
        evidences: data.evidences.map(evidence =>
          evidence.evidence_id === evidenceId
            ? { ...evidence, files: evidence.files.filter(f => f.fileID !== file.fileID) }
            : evidence
        )
      });
    } catch (error) {
      console.error('Delete error:', error);

      let errorMessage = 'Failed to delete file. Please try again.';
      if (error.response?.status === 404) {
        errorMessage = 'File not found or already deleted.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.message === 'Failed to fetch') {
        errorMessage = 'Cannot connect to server. Please check your connection.';
      }

      alert(errorMessage);
    } finally {
      setdeletedFiles([]);
    }
  }

  const handleFileChangeForEvidence = async (event, evidenceId, evidenceTitle) => {
    const files = Array.from(event.target.files);
    setTokenLimitError(null);

    if (files.length === 0) return;

    // Validation for EV03: must be Excel
    if (evidenceTitle === "EV03") {
      const invalidFiles = files.filter(file => !file.name.toLowerCase().endsWith(".xlsx"));
      if (invalidFiles.length > 0) {
        setErrors(prev => ({
          ...prev,
          [`evidence_${evidenceId}`]: "Only Excel (.xlsx) files are allowed."
        }));
        event.target.value = "";
        return;
      }
    }

    setErrors(prev => {
      const copy = { ...prev };
      delete copy[`evidence_${evidenceId}`];
      return copy;
    });

    // Validate all files
    const filesWithErrors = [];
    const validFiles = [];

    for (const file of files) {
      const validationErrors = validateFile(file);
      if (validationErrors.length > 0) {
        filesWithErrors.push({ file, errors: validationErrors });
      } else {
        validFiles.push(file);
      }
    }

    if (filesWithErrors.length > 0) {
      const errorMessage = `The following files have issues: ${filesWithErrors.map(f => `${f.file.name}: ${f.errors.join(', ')}`).join('; ')}`;
      setErrors(prev => ({ ...prev, [`evidence_${evidenceId}`]: errorMessage }));
    }

    if (validFiles.length === 0) {
      event.target.value = '';
      return;
    }

    const processingData = validFiles.map(f => ({ fileName: f.name, evidenceId }));
    setprocessingFiles(processingData);

    try {
      // Upload files to original system only (RMS sync happens during final submission)
      const result = await uploadFilesToOriginalSystem(
        validFiles,
        evidenceId,
        evidenceTitle
      );

      if (result && result.uploadedFiles) {
        setData({
          ...data,
          evidences: data.evidences.map(evidence =>
            evidence.evidence_id === evidenceId
              ? {
                ...evidence,
                files: [...(evidence.files || []), ...result.uploadedFiles]
              }
              : evidence
          )
        });
      }

      event.target.value = '';
    } catch (error) {
      console.error('Upload failed:', error);

      let errorMessage = 'Upload failed';
      if (error.message === 'Failed to fetch') {
        errorMessage = 'Cannot connect to server. Please check your connection.';
      } else if (error.response) {
        errorMessage = `Upload failed: ${error.response.status} - ${error.response.data?.message || error.message}`;
      } else {
        errorMessage = `Upload failed: ${error.message}`;
      }

      setTokenLimitError(errorMessage);
      event.target.value = '';
    } finally {
      setprocessingFiles([]);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(2) + ' KB';
    else return (bytes / 1048576).toFixed(2) + ' MB';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const generateExpectedFilename = (description) => {
    if (!description) return "document.pdf";
    const firstPart = description.split(/[,.]/)[0].trim();
    return `${firstPart}.pdf`;
  };

  const validateEvidenceFiles = () => {
    if (process.env.REACT_APP_FRAMEWORK == 2 && data.evidences) {
      const evidencesWithoutNote = data.evidences.filter(evidence => evidence.evidence_title !== "Note");
      return evidencesWithoutNote.every(evidence => evidence.files && evidence.files.length > 0);
    }
    return true;
  };

  const getWordCount = (text) => {
    if (!text) return 0;
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const isWordLimitExceeded = (text) => process.env.REACT_APP_FRAMEWORK == 2 && getWordCount(text) > 250;

  const validateWordLimit = () => process.env.REACT_APP_FRAMEWORK != 2 || !isWordLimitExceeded(data.text);

  useEffect(() => {
    if (setValidateEvidenceFiles) {
      setValidateEvidenceFiles(() => () => validateEvidenceFiles() && validateWordLimit());
    }
  }, [data.evidences, data.text, setValidateEvidenceFiles]);

  useEffect(() => {
    if (setJudgmentValidation) {
      setJudgmentValidation(() => () => !!judgment);
    }
  }, [judgment, setJudgmentValidation]);

  useEffect(() => {
    if (setValidateWordLimit) {
      setValidateWordLimit(() => () => validateWordLimit());
    }
  }, [data.text, setValidateWordLimit]);

  useEffect(() => {
    if (data.judgment?.judgment) {
      setJudgment(data.judgment.judgment);
    }
  }, [data.judgment]);

  return (
    <Container
      id="distribution-panel"
      header={
        <Header
          variant="h2"
          actions={
            process.env.REACT_APP_FRAMEWORK == 2 && (
              <SpaceBetween direction="horizontal" size="xs">
                <ButtonDropdown
                  items={[
                    { id: "Outstanding", text: "Outstanding" },
                    { id: "Good", text: "Good" },
                    { id: "Satisfactory", text: "Satisfactory" },
                    { id: "Inadequate", text: "Inadequate" },
                  ]}
                  onItemClick={({ detail }) => {
                    const selected = detail.id;
                    setJudgment(selected);
                    submissionsApi
                      .updateIndicatorjudgment(data.id, selected)
                      .catch((err) => console.error("Error updating judgment:", err));
                  }}
                >
                  {judgment ? `Judgment: ${judgment}` : "Set Judgment"}
                </ButtonDropdown>            </SpaceBetween>)
          }
        >
          {data.indicator?.index}: {data.indicator?.name}
        </Header>
      }
    >

      <SpaceBetween size="l">
        <Container
          header={
            <Header
              variant="h2"
              description={data.indicator?.description}
            >
              {process.env.REACT_APP_FRAMEWORK == 1 ? "Description" : "Expectations"}
            </Header>
          }
        ></Container>
        {process.env.REACT_APP_FRAMEWORK == 1 && (
          <Container
            header={
              <Header
                variant="h2"
              >
                Indicator Checklist
              </Header>
            }
          >
            {data.indicator?.extracted_admin_checklist && (
              <div>
                {data.indicator.extracted_admin_checklist.split(',').map((line, index) => (
                  <p key={index} className="mt-2">
                    {line.trim()}
                  </p>
                ))}
              </div>
            )}
          </Container>
        )}


        <FormField
          label={
            <>
              Institution's Description
            </>
          }
          // info={<InfoLink id="institution-description-info" onFollow={()=>setIsCustomModalVisible(true)}/>}
          description="Describe your institution’s processes and practices related to this indicator (Maximum 250 words)"
          stretch={true}
          i18nStrings={{ errorIconAriaLabel: 'Error' }}
          errorText={errors.text}
        >
          <Textarea
            rows={10}
            value={data.text}
            onChange={({ detail: { value } }) => onChange('text', value)}
            onBlur={() => onBlur('text')}
            ref={refs.text}
            invalid={isWordLimitExceeded(data.text)}
          />
          {process.env.REACT_APP_FRAMEWORK == 2 && (
            <>
              {isWordLimitExceeded(data.text) && (
                <div style={{
                  fontSize: '12px',
                  color: '#d91515',
                  marginTop: '4px'
                }}>
                  Error: Text exceeds the 250-word limit. Please reduce the content.
                </div>
              )}
              <div style={{
                fontSize: '12px',
                color: isWordLimitExceeded(data.text) ? '#d91515' : '#666',
                marginTop: '4px'
              }}>
                {`${getWordCount(data.text)}/250 words`}
              </div>
            </>
          )}
        </FormField>
        {tokenLimitError && (
          <Alert type="error">
            {tokenLimitError}
          </Alert>
        )}

        {(tokenUsage !== null && process.env.REACT_APP_FRAMEWORK == 1) && (
          <Alert type="info">
            Current token usage: {tokenUsage}%
          </Alert>
        )}
        {data.evidences && data.evidences.length > 0 && data.evidences.filter(evidence => evidence.evidence_title !== "Note").map((evidence, evidenceIndex) => (
          <FormField
            key={evidence.evidence_id}
            label={evidence.evidence_title}
            description={evidence.evidence_description?.split('.')[0]?.trim()}
            i18nStrings={{ errorIconAriaLabel: 'Error' }}
            required

          >
            <SpaceBetween size="m">
              <div>
                <input
                  type="file"
                  onChange={(e) => handleFileChangeForEvidence(e, evidence.evidence_id, evidence.evidence_title)}
                  multiple
                  accept=".txt,.csv,.png,.jpg,.pdf,.docx,.xlsx"
                  disabled={processingFiles.length > 0}
                />

                {(() => {
                  const isProcessing = processingFiles.some(pf => pf.evidenceId === evidence.evidence_id);
                  return isProcessing;
                })() && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                      <Spinner size="normal" /> Processing file(s)...
                    </div>
                  )}

                <div style={{ fontSize: '12px', color: '#666' }}>
                  {evidence.evidence_title === "EV03"
                    ? "You must upload Excel files (.xlsx). You can select multiple files at once."
                    :
                    "You can upload multiple documents at once in the following formats (.docx, .txt, .csv, .xlsx, .png, .jpg, .pdf)"}
                </div>
                {/* {process.env.REACT_APP_FRAMEWORK == 2 && (
                  // <div style={{ marginTop: '4px', fontSize: '12px', color: '#666' }}>
                  //   <strong>Please follow the naming convention outlined in the description for the uploaded files: e.g: "</strong>{generateExpectedFilename(evidence.evidence_description)}<strong>"</strong>
                  // </div>
                )} */}

                {(errors[evidence.evidence_id] || errors[`evidence_${evidence.evidence_id}`]) && (
                  <Alert type="error" style={{ marginTop: '8px' }}>
                    {typeof (errors[evidence.evidence_id] || errors[`evidence_${evidence.evidence_id}`]) === 'string'
                      ? (errors[evidence.evidence_id] || errors[`evidence_${evidence.evidence_id}`])
                      : JSON.stringify(errors[evidence.evidence_id] || errors[`evidence_${evidence.evidence_id}`])}
                  </Alert>
                )}
              </div>



              {evidence.files && evidence.files.length > 0 && (
                process.env.REACT_APP_FRAMEWORK == 2 ? (
                  <ExpandableSection headerText={`Uploaded Files (${evidence.files.length})`} defaultExpanded={false}>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                      {evidence.files.map((file) => (
                        <li key={file.fileID} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                          <div>{file.file.name}</div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <Button
                              variant="icon"
                              iconName="download"
                              ariaLabel={`Download ${file.file.name}`}
                              onClick={async () => {
                                try {
                                  const response = await submissionsApi.downloadUploadedFile(file.fileID);
                                  const link = document.createElement('a');
                                  link.href = response.url;
                                  link.download = file.file.name;
                                  link.click();
                                } catch (err) {
                                  console.error('Download error:', err);
                                  alert('Failed to download file');
                                }
                              }}
                            />
                            <Button
                              variant="icon"
                              iconName="remove"
                              onClick={() => deleteFile(file, evidence.evidence_id)}
                              ariaLabel={`Remove ${file.file.name}`}
                              disabled={deletedFiles.length > 0}
                            />
                          </div>
                        </li>
                      ))}
                    </ul>
                  </ExpandableSection>
                ) : (
                  <Box padding="s" backgroundColor="light">
                    <h4 style={{ marginTop: 0 }}>Uploaded Files</h4>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                      {evidence.files.map((file) => (
                        <li key={file.fileID} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                          <div>{file.file.name}</div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <Button
                              variant="icon"
                              iconName="download"
                              ariaLabel={`Download ${file.file.name}`}
                              onClick={async () => {
                                try {
                                  const response = await submissionsApi.downloadUploadedFile(file.fileID);
                                  const link = document.createElement('a');
                                  link.href = response.url;
                                  link.download = file.file.name;
                                  link.click();
                                } catch (err) {
                                  console.error('Download error:', err);
                                  alert('Failed to download file');
                                }
                              }}
                            />
                            <Button
                              variant="icon"
                              iconName="remove"
                              onClick={() => deleteFile(file, evidence.evidence_id)}
                              ariaLabel={`Remove ${file.file.name}`}
                              disabled={deletedFiles.some(pf => pf.fileName === file.file.name) || processingFiles.length > 0}
                            />
                          </div>
                        </li>
                      ))}

                    </ul>
                  </Box>
                )
              )}
            </SpaceBetween>
          </FormField>
        ))}


        {(process.env.REACT_APP_FRAMEWORK == 2 && data.evidences && data.evidences.length > 0) && (
          <Alert type="info" header="Note">
            {data.indicator?.index?.startsWith('2.1') ?
              'These expectations will also be assessed through the review team\'s observation of training sessions conducted during the site visit.' :
              'All evidence will be further assessed by BQA'
            }
          </Alert>
        )}


        <BroadcastSubscriptionAdapter submissionId={data.id} />
        <CustomModal
          visible={isCustomModalVisible}
          onDismiss={() => setIsCustomModalVisible(false)}
          message={customModalMessage}
        />
      </SpaceBetween>
    </Container>
  );
}